import moment from 'moment';
import { useMemo, useState, useCallback } from 'react';
import {
    Bar,
    DatafeedConfiguration,
    IBasicDataFeed,
    LibrarySymbolInfo,
    ResolutionString,
    SubscribeBarsCallback,
} from '../../charting_library';
import chartConfig from './chart.config';
import { convertInterval } from './chart.helpers';
import { sleep } from '../../helpers';

type kLinesType = (string | number)[];

const configurationData: DatafeedConfiguration = {
    supports_marks: false,
    supports_timescale_marks: false,
    supports_time: true,
    supported_resolutions: chartConfig.supported_resolutions,
};

const startPrices: Record<string, number> = {
    BTCUSDC: 65000,
    ETHUSDC: 3500,
    LINKUSDC: 15,
    BNBUSDC: 600,
    XRPUSDC: 0.5,
    ADAUSDC: 0.4,
    MATICUSDC: 0.7,
    DOTUSDC: 7,
    ARBUSDC: 1.2,
    DOGEUSDC: 0.15,
    UNIUSDC: 10,
};

const generateHistoricalKlines = (
    symbol: string,
    interval: string,
    from: number,
    to: number
): kLinesType[] => {
    const startPrice = startPrices[symbol] ?? 1000;
    const intervalMs = parseIntervalToMs(interval);
    const klines: kLinesType[] = [];

    let currentTime = from;
    let price = startPrice;

    while (currentTime <= to) {
        const volatility = price * 0.005;
        const open = price;
        const high = open + Math.random() * volatility;
        const low = open - Math.random() * volatility;
        const close = low + Math.random() * (high - low);
        const volume = Math.random() * 1000 + 100;

        klines.push([
            currentTime,
            open,
            high,
            low,
            close,
            volume,
            currentTime + intervalMs,
            currentTime,
        ]);

        price = close;
        currentTime += intervalMs;
    }

    return klines;
};

const parseIntervalToMs = (interval: string): number => {
    const match = interval.match(/^(\d+)([smhd])$/);
    if (!match) return 86400000;

    const value = parseInt(match[1]);
    const unit = match[2];

    switch (unit) {
        case 's':
            return value * 1000;
        case 'm':
            return value * 60 * 1000;
        case 'h':
            return value * 60 * 60 * 1000;
        case 'd':
            return value * 24 * 60 * 60 * 1000;
        default:
            return 86400000;
    }
};

type StreamEntry = {
    paramStr: string;
    listener: SubscribeBarsCallback;
    symbolInfo: LibrarySymbolInfo;
    resolution: ResolutionString;
    currentBar: Bar;
    interval: string;
};

let activeStreams: Record<string, StreamEntry> = {};
let intervalId: ReturnType<typeof setInterval> | null = null;
let lastCloseCache: Record<string, number> = {};

const startStreamUpdates = () => {
    if (intervalId) return;

    intervalId = setInterval(() => {
        Object.entries(activeStreams).forEach(([uid, stream]) => {
            const { currentBar, listener, interval } = stream;
            const intervalMs = parseIntervalToMs(interval);
            const now = Date.now();
            const barStart = Math.floor(now / intervalMs) * intervalMs;

            const lastClose = lastCloseCache[uid] ?? currentBar.close;
            const volatility = lastClose * 0.002;
            const newClose = lastClose + (Math.random() * 2 - 1) * volatility;
            const newHigh = Math.max(currentBar.high, newClose);
            const newLow = Math.min(currentBar.low, newClose);

            const isNewBar = barStart !== currentBar.time;
            const barOpen = isNewBar ? lastClose : currentBar.open;

            const updatedBar: Bar = {
                time: barStart,
                open: barOpen,
                high: newHigh,
                low: newLow,
                close: newClose,
                volume: (currentBar.volume ?? 100) + Math.random() * 10,
            };

            lastCloseCache[uid] = newClose;

            stream.currentBar = updatedBar;
            listener(updatedBar);
        });
    }, 1000);
};

const stopStreamUpdates = () => {
    if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
    }
};

const useApi = (debug: boolean = false) => {
    const [, setStreamCount] = useState(0);

    const subscribeOnStream = useCallback(
        (
            symbolInfo: LibrarySymbolInfo,
            resolution: ResolutionString,
            onRealtimeCallback: SubscribeBarsCallback,
            subscribeUID: string,
            onResetCacheNeededCallback: () => void
        ) => {
            try {
                const interval = convertInterval(resolution);
                if (!interval) return false;

                const now = Date.now();
                const intervalMs = parseIntervalToMs(interval);
                const barStart = Math.floor(now / intervalMs) * intervalMs;
                const startPrice = startPrices[symbolInfo.name] ?? 1000;

                const initialBar: Bar = {
                    time: barStart,
                    open: startPrice,
                    high: startPrice * 1.001,
                    low: startPrice * 0.999,
                    close: startPrice,
                    volume: 100,
                };

                activeStreams[subscribeUID] = {
                    paramStr: `${symbolInfo.name.toLowerCase()}@kline_${interval}`,
                    listener: onRealtimeCallback,
                    symbolInfo,
                    resolution,
                    currentBar: initialBar,
                    interval,
                };

                lastCloseCache[subscribeUID] = startPrice;

                setStreamCount(Object.keys(activeStreams).length);
                startStreamUpdates();

                onRealtimeCallback(initialBar);

                return true;
            } catch (e) {
                console.error(e);
                return false;
            }
        },
        []
    );

    const unsubscribeFromStream = useCallback((subscriberUID: string) => {
        try {
            delete activeStreams[subscriberUID];
            delete lastCloseCache[subscriberUID];
            setStreamCount(Object.keys(activeStreams).length);

            if (Object.keys(activeStreams).length === 0) {
                stopStreamUpdates();
            }
        } catch (e) {
            console.error(e);
        }
    }, []);

    const datafeed: IBasicDataFeed | undefined = useMemo(
        () => ({
            onReady: (callback) => {
                debug && console.log('[onReady]: Method call');
                setTimeout(() => callback(configurationData), 0);
            },
            searchSymbols: async (
                userInput,
                exchange,
                symbolType,
                onResultReadyCallback
            ) => {
                onResultReadyCallback([]);
            },
            resolveSymbol: async (
                symbolName,
                onSymbolResolvedCallback,
                onResolveErrorCallback
            ) => {
                debug && console.log('[resolveSymbol]: Method call', symbolName);

                const symbols = chartConfig.symbols;

                if (!symbols || !configurationData.supported_resolutions) return;

                const symbolItem = symbols.find(({ symbol }) => symbol === symbolName);

                if (!symbolItem) {
                    debug &&
                        console.log('[resolveSymbol]: Cannot resolve symbol', symbolName);
                    onResolveErrorCallback('cannot resolve symbol');
                    return;
                }

                const symbolInfo = {
                    name: symbolItem.symbol,
                    description: symbolItem.description,
                    ticker: symbolItem.symbol,
                    full_name: symbolItem.full_name,
                    exchange: 'PriceFeed',
                    listed_exchange: 'PriceFeed',
                    type: 'crypto',
                    session: '24x7',
                    minmov: 1,
                    timezone: 'Etc/UTC',
                    pricescale: symbolItem.pricescale,
                    has_intraday: true,
                    has_daily: true,
                    has_weekly_and_monthly: true,
                    has_seconds: true,
                };

                debug && console.log('[resolveSymbol]: Symbol resolved', symbolName);

                setTimeout(
                    () => onSymbolResolvedCallback(symbolInfo as LibrarySymbolInfo),
                    0
                );
            },
            getBars: async (
                symbolInfo,
                resolution,
                periodParams,
                onHistoryCallback,
                onErrorCallback
            ) => {
                let { from, to } = periodParams;

                const interval = convertInterval(resolution);

                if (!interval) {
                    onErrorCallback('Invalid interval');
                    return;
                }

                from *= 1000;
                to *= 1000;

                try {
                    const data = generateHistoricalKlines(
                        symbolInfo.name,
                        interval,
                        from,
                        to
                    );

                    debug &&
                        console.log(
                            `[getBars]: Returned ${data.length} bars from ${moment
                                .unix(from / 1000)
                                .format('DD-MM-YYYY')} to ${moment
                                .unix(to / 1000)
                                .format('DD-MM-YYYY')}`
                        );

                    if (data.length === 0) {
                        onHistoryCallback([], { noData: true });
                    } else {
                        const historyCBArray: Bar[] = data.map((kline) => ({
                            time: Number(kline[0]),
                            open: parseFloat(kline[1].toString()),
                            high: parseFloat(kline[2].toString()),
                            low: parseFloat(kline[3].toString()),
                            close: parseFloat(kline[4].toString()),
                            volume: parseFloat(kline[5].toString()),
                        }));
                        onHistoryCallback(historyCBArray, { noData: false });
                    }
                } catch (e) {
                    console.error(e);
                    onErrorCallback(`Error in 'getBars' func`);
                }
            },
            subscribeBars: async (
                symbolInfo,
                resolution,
                onRealtimeCallback,
                subscribeUID,
                onResetCacheNeededCallback
            ) => {
                debug &&
                    console.log(
                        '[subscribeBars]: Method call with subscribeUID:',
                        subscribeUID
                    );

                let count = 0;

                while (count < 5) {
                    const subscribed = subscribeOnStream(
                        symbolInfo,
                        resolution,
                        onRealtimeCallback,
                        subscribeUID,
                        onResetCacheNeededCallback
                    );

                    if (subscribed) {
                        break;
                    }

                    await sleep(3000);
                    count++;
                }
            },
            unsubscribeBars: (subscriberUID) => {
                debug &&
                    console.log(
                        '[unsubscribeBars]: Method call with subscriberUID:',
                        subscriberUID
                    );
                unsubscribeFromStream(subscriberUID);
            },
        }),
        [debug, subscribeOnStream, unsubscribeFromStream]
    );

    return { datafeed };
};

export default useApi;
